import React from "react";
import { Link } from "react-router-dom";
import RoboticsShell from "./RoboticsShell";
import NewsletterForm from "./NewsletterForm";

/**
 * Robotics landing page — Phase 2 polish.
 *
 *  - Big display tagline
 *  - Clarke heritage strip
 *  - Warm parents sub-line
 *  - Coming-Q4-2026 chips on unfinished CTAs
 *  - Newsletter capture
 *  - Bench Blueprint download strip
 */
export default function RoboticsLanding() {
  return (
    <RoboticsShell>
      <div className="rbx-blueprint-strip">
        Phase 0 planning complete ·{" "}
        <a href="/robotics-plan/" target="_blank" rel="noopener noreferrer" data-testid="rbx-blueprint-link">
          Curriculum blueprint available →
        </a>
      </div>

      {/* FutureTec Robotics brand hero. AI-generated (Nano Banana),
          directed by Niranjan Nanayakkara Gamage.
          © 2026 FutureTec Robotics Academy. */}
      <section className="rbx-brand-hero" data-testid="rbx-brand-hero">
        <picture>
          <source
            type="image/webp"
            srcSet="/robotics/brand/hero_kids_workbench.webp"
          />
          <img
            src="/robotics/brand/hero_kids_workbench.jpg"
            alt="Two Sri Lankan children at a workbench, building an electronics circuit that rises into a Clarke satellite orbit"
            className="rbx-brand-hero__img"
            loading="eager"
            fetchpriority="high"
            data-testid="rbx-brand-hero-img"
          />
        </picture>
        <div className="rbx-brand-hero__scrim" aria-hidden="true" />
        <div className="rbx-brand-hero__copy" data-testid="rbx-brand-hero-copy">
          <span className="rbx-brand-hero__eyebrow" data-testid="rbx-brand-hero-eyebrow">
            FutureTec Robotics Academy
          </span>
          <h1 className="rbx-brand-hero__title" data-testid="rbx-brand-hero-title">
            Robotics from<br />first principles.
          </h1>
          <p className="rbx-brand-hero__sub" data-testid="rbx-brand-hero-sub">
            Ages 8&ndash;17. Wherever you are. Whoever you are. Your genius starts here.
          </p>
        </div>
      </section>

      <header className="rbx-hero">
        <span className="rbx-eyebrow rbx-hero__eyebrow" data-testid="rbx-hero-eyebrow">
          FutureTec Robotics Academy · Phase 2 preview
        </span>
        <h1 className="rbx-hero__title" data-testid="rbx-hero-title">
          Robots can think deep and our robotics students <em>also can</em> —
          as they know how robots work inside out.
        </h1>
        <p className="rbx-hero__tagline" data-testid="rbx-hero-tagline">
          A first-principles engineering curriculum for ages 8 to 17. Every
          component opened up, every chip explained, every line of code
          understood. For curious kids and the parents who believe in them.
        </p>
        <div className="rbx-hero__actions">
          <span className="rbx-btn rbx-btn--primary" data-testid="rbx-cta-primary" role="button" aria-disabled="true"
                style={{ opacity: 0.75, cursor: "not-allowed" }}>
            Explore the curriculum <span className="rbx-chip-q4" style={{ marginLeft: 8 }}>Coming Q4 2026</span>
          </span>
          <a
            className="rbx-btn rbx-btn--ghost"
            href="/robotics-plan/"
            target="_blank" rel="noopener noreferrer"
            data-testid="rbx-cta-secondary"
          >
            Read the blueprint (33-page PDF)
          </a>
        </div>

        <svg
          className="rbx-hero__satellite"
          viewBox="0 0 400 400"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="200" cy="200" r="140" fill="none"
                  stroke="var(--rbx-phosphor)" strokeWidth="0.7"
                  strokeDasharray="3 5" opacity="0.55" />
          <circle cx="200" cy="200" r="90" fill="none"
                  stroke="var(--rbx-gold)" strokeWidth="0.6"
                  strokeDasharray="2 4" opacity="0.55" />
          <circle cx="200" cy="200" r="46" fill="none"
                  stroke="var(--rbx-copper)" strokeWidth="0.8" opacity="0.7" />
          <ellipse cx="200" cy="200" rx="46" ry="15" fill="none"
                   stroke="var(--rbx-copper)" strokeWidth="0.5" opacity="0.55" />
          <ellipse cx="200" cy="200" rx="15" ry="46" fill="none"
                   stroke="var(--rbx-copper)" strokeWidth="0.5" opacity="0.55" />
          <g fill="var(--rbx-phosphor)">
            <rect x="336" y="197" width="8" height="6" />
            <rect x="60" y="330" width="8" height="6" transform="rotate(120 200 200)" />
            <rect x="60" y="70"  width="8" height="6" transform="rotate(240 200 200)" />
          </g>
        </svg>
      </header>

      {/* "Our Promise" manifesto — universal positioning, user-approved verbatim. */}
      <section className="rbx-promise" data-testid="rbx-promise">
        <div className="rbx-promise__inner">
          <h2 className="rbx-promise__title" data-testid="rbx-promise-title">Our promise</h2>
          <p className="rbx-promise__opener" data-testid="rbx-promise-opener">
            Wherever you are. Whoever you are.
          </p>
          <p className="rbx-promise__body">
            One thing binds us all: the desire to explore this new
            AI-driven world we&rsquo;re building together.
          </p>
          <p className="rbx-promise__body">
            There is a genius inside every child. Your quest is to bring
            that genius out &mdash; and release it to the new world.
          </p>
          <p className="rbx-promise__body">
            We are here to facilitate that quest. From the United Kingdom,{" "}
            <strong>Qualification Evaluation UK Ltd.</strong> &mdash; trading
            as <strong>FutureTec Robotics Academy</strong> &mdash; fulfils
            the dream of every genius boy and every genius girl, wherever
            they live.
          </p>
        </div>
      </section>

      <div className="rbx-heritage">
        <div className="rbx-heritage__inner" data-testid="rbx-heritage-line">
          In the tradition of <strong>Sir Arthur C. Clarke</strong>&rsquo;s Sri Lankan
          legacy. Founded by the team that organised the <strong>first LEGO
          Robotics Olympiad in Sri Lanka</strong>, in 2004 &mdash; decades before
          &ldquo;STEM&rdquo; was a hashtag.
          {" "}
          <Link
            to="/robotics/heritage"
            className="rbx-heritage__cta"
            data-testid="rbx-heritage-cta"
          >
            Watch the Clarke Legacy &rarr;
          </Link>
        </div>
      </div>
      <p className="rbx-parents-subline" data-testid="rbx-parents-subline">
        For curious kids and the parents who believe in them.
      </p>

      <section className="rbx-bands" aria-labelledby="rbx-bands-heading">
        <h2
          id="rbx-bands-heading"
          className="rbx-page__title"
          style={{ fontSize: "28px", marginBottom: "8px" }}
          data-testid="rbx-bands-heading"
        >
          Three bands. Ninety lessons. One path.
        </h2>
        <p style={{ color: "var(--rbx-ink-mute)", maxWidth: "60ch", marginBottom: "32px" }}>
          Content generation begins in Phase 3 — these are the shells that
          will hold it.
        </p>
        <div className="rbx-bands__grid">
          {BANDS.map((b) => (
            <article key={b.key} className="rbx-card" data-testid={`rbx-band-${b.key}`}>
              <div className="rbx-band__num">BAND · {b.num}</div>
              <div className="rbx-band__age">{b.age}</div>
              <div className="rbx-band__name">{b.name}</div>
              <p className="rbx-band__blurb">{b.blurb}</p>
            </article>
          ))}
        </div>

        {/* Bench blueprint download strip */}
        <div className="rbx-bench-strip" data-testid="rbx-bench-strip">
          <div className="rbx-bench-strip__blurb">
            <strong>The Bench Blueprint.</strong> A single-page A3 workbench
            plan you can print, hand a carpenter, and scale to any room. Free
            for schools, homeschoolers, makers.
          </div>
          <a
            className="rbx-btn rbx-btn--primary"
            href="/api/downloads/robotics_workbench_blueprint.pdf"
            data-testid="rbx-bench-download"
          >
            Download The Bench Blueprint
          </a>
        </div>

        {/* Newsletter */}
        <div style={{ marginTop: 40, maxWidth: 620 }}>
          <p style={{ color: "var(--rbx-ink-mute)", fontSize: 14, marginBottom: 8 }}>
            One quiet email when the curriculum launches. No noise, no drip
            sequence. Unsubscribe from any message.
          </p>
          <NewsletterForm />
        </div>

        {/* Girls@TEC nudge — non-metallic, warm */}
        <div style={{ marginTop: 40, padding: 22, borderRadius: 18,
                      background: "linear-gradient(120deg, rgba(244,196,106,0.10), rgba(242,139,122,0.06))",
                      border: "1px solid rgba(244,196,106,0.28)" }}
             data-testid="rbx-girls-nudge">
          <div style={{ fontFamily: "var(--rbx-font-display)", fontSize: 22, fontWeight: 600, marginBottom: 6 }}>
            Girls@TEC — core, not add-on.
          </div>
          <p style={{ color: "var(--rbx-ink-mute)", maxWidth: "62ch", marginBottom: 14 }}>
            Girls are naturally strong in mathematics. We don&rsquo;t want a single
            one dropping out because of social stigma.
          </p>
          <Link className="rbx-btn rbx-btn--ghost" to="/robotics/girls-at-tec" data-testid="rbx-girls-cta">
            Read why we exist →
          </Link>
        </div>
      </section>
    </RoboticsShell>
  );
}

const BANDS = [
  { key: "explorer", num: "01 / 03", age: "Ages 8 — 11",  name: "Explorer",
    blurb: "Physical intuition. Micro:bit + breadboard. First LED, first sensor, first motor. Ends with the Polite Line-Follower." },
  { key: "builder",  num: "02 / 03", age: "Ages 12 — 14", name: "Builder",
    blurb: "Arduino Uno + real analog electronics. Transistors, op-amps, oscilloscope. Ends with the Smart Line-Follower 2.0." },
  { key: "engineer", num: "03 / 03", age: "Ages 15 — 17", name: "Engineer",
    blurb: "Raspberry Pi Pico + Python. I²C/SPI, PID, filters, computer vision. Ends with the vision-based Line-Follower Mark III." },
];
